import React, { useMemo, useState } from 'react';
import styled from '@emotion/styled';
import moment from 'moment';
import ApplicationModel from '../../../../../shared/models/applications/api/ApplicationModel';
import Button from '../../../../../shared/components/general/Button';
import Loader from '../../../../../shared/components/general/Loader';
import TooltipWrapper from '../../../../../shared/components/general/TooltipWrapper';
import {
    color_colors_ocean,
    color_colors_growth,
    color_colors_gold
} from '../../../../../shared/constants/colors';

export interface OnboardingActionItem {
    id: string;
    isOrg?: boolean;
    app: ApplicationModel;
    action: {
        prefix: string;
        suffix?: string;
        type: string;
        triggerId?: string;
        buttonText?: string;
        themes?: Array<string>;
        disabled?: boolean;
        onClick?: () => void;
        isOrgOnboardingInvitation?: boolean;
    };
    index?: number;
}

export interface OnboardingActionRequiredSectionProps {
    activeActions: OnboardingActionItem[];
    isLoading?: boolean;
    onGoToLocation?: (locationId: string) => void;
    onGoToOrganization?: () => void;
}

export type ActionPriorityTier = 'critical' | 'high' | 'medium' | 'standard';
export type ActionCategoryKey = 'all' | 'critical' | 'launch' | 'outbound' | 'qa' | 'invites';
export type ActionViewMode = 'location' | 'priority' | 'flat';
export type LocationSortOption = 'alpha' | 'priority' | 'count';

export interface EnrichedActionItem extends OnboardingActionItem {
    priorityTier: ActionPriorityTier;
    priorityScore: number;
    categoryKey: ActionCategoryKey;
    categoryLabel: string;
    categoryBadgeColor: {
        bg: string;
        text: string;
        border: string;
    };
}

const getActionEnrichment = (item: OnboardingActionItem): {
    priorityTier: ActionPriorityTier;
    priorityScore: number;
    categoryKey: ActionCategoryKey;
    categoryLabel: string;
    categoryBadgeColor: { bg: string; text: string; border: string };
} => {
    const { action, isOrg } = item;
    const prefix = action.prefix || '';
    const buttonText = action.buttonText || '';
    const triggerId = action.triggerId || '';

    // 1. Disable Live Location Link (Critical security/cleanup)
    if (prefix.includes('Disable onboarding links') || buttonText === 'Disable Link') {
        return {
            priorityTier: 'critical',
            priorityScore: 100,
            categoryKey: 'critical',
            categoryLabel: 'Disable Link',
            categoryBadgeColor: { bg: '#fee2e2', text: '#b91c1c', border: '#fca5a5' }
        };
    }

    // 2. Client Feedback Review (Critical blocker - client waiting on Voicify review)
    if (prefix.includes('Review Round 1 Feedback') || prefix.includes('Review Round 2 Feedback') || buttonText === 'Review Feedback') {
        return {
            priorityTier: 'critical',
            priorityScore: 90,
            categoryKey: 'critical',
            categoryLabel: 'Feedback Review',
            categoryBadgeColor: { bg: '#fef3c7', text: '#b45309', border: '#fcd34d' }
        };
    }

    // 3. Go-Live Notification
    if (triggerId === 'go-live' || prefix.includes('Send Go Live Notification')) {
        return {
            priorityTier: 'critical',
            priorityScore: 85,
            categoryKey: 'launch',
            categoryLabel: 'Go-Live',
            categoryBadgeColor: { bg: '#dcfce7', text: '#15803d', border: '#86efac' }
        };
    }

    // 4. Launch Ready Notification
    if (triggerId === 'launch-ready' || prefix.includes('Send Launch Ready Notification')) {
        return {
            priorityTier: 'high',
            priorityScore: 80,
            categoryKey: 'launch',
            categoryLabel: 'Launch Ready',
            categoryBadgeColor: { bg: '#dcfce7', text: '#166534', border: '#86efac' }
        };
    }

    // 5. Spark Dashboard Invite
    if (item.id.includes('spark-dashboard-invite') || prefix.includes('Invite Client to Spark Dashboard') || buttonText === 'Invite Client') {
        return {
            priorityTier: 'high',
            priorityScore: 75,
            categoryKey: 'launch',
            categoryLabel: 'Dashboard Invite',
            categoryBadgeColor: { bg: '#e0e7ff', text: '#4338ca', border: '#a5b4fc' }
        };
    }

    // 6. Outbound Onboarding Form Review & Enable
    if (item.id.includes('outbound-review-enable') || prefix.includes('Review outbound onboarding form')) {
        return {
            priorityTier: 'high',
            priorityScore: 70,
            categoryKey: 'outbound',
            categoryLabel: 'Outbound Review',
            categoryBadgeColor: { bg: '#ede9fe', text: '#6d28d9', border: '#c4b5fd' }
        };
    }

    // 7. Automated QA & Shared Config
    if (prefix.includes('Run Automated Tests') || prefix.includes('Complete Shared Config & Run Automated QA') || buttonText === 'Run Tests') {
        return {
            priorityTier: 'medium',
            priorityScore: 60,
            categoryKey: 'qa',
            categoryLabel: 'Automated QA',
            categoryBadgeColor: { bg: '#e0f2fe', text: '#0369a1', border: '#7dd3fc' }
        };
    }

    // 8. Testing Invitation
    if (triggerId === 'testing-ready' || triggerId === 'phase2-testing-ready' || prefix.includes('Testing Invitation')) {
        return {
            priorityTier: 'medium',
            priorityScore: 50,
            categoryKey: 'qa',
            categoryLabel: 'Testing Invite',
            categoryBadgeColor: { bg: '#e0f2fe', text: '#0284c7', border: '#bae6fd' }
        };
    }

    // 9. Organization Onboarding Form
    if (isOrg || triggerId === 'org-launched' || prefix.includes('Organization Onboarding')) {
        return {
            priorityTier: 'medium',
            priorityScore: 45,
            categoryKey: 'invites',
            categoryLabel: 'Org Onboarding',
            categoryBadgeColor: { bg: '#eff6ff', text: '#1d4ed8', border: '#bfdbfe' }
        };
    }

    // 10. Location Onboarding Invitation
    if (triggerId === 'loc-launched' || prefix.includes('Send Onboarding Invitation')) {
        return {
            priorityTier: 'standard',
            priorityScore: 30,
            categoryKey: 'invites',
            categoryLabel: 'Onboarding Invite',
            categoryBadgeColor: { bg: '#f1f5f9', text: '#334155', border: '#cbd5e1' }
        };
    }

    // 11. Reminders & default
    return {
        priorityTier: 'standard',
        priorityScore: 20,
        categoryKey: 'invites',
        categoryLabel: 'Reminder',
        categoryBadgeColor: { bg: '#f8fafc', text: '#475569', border: '#e2e8f0' }
    };
};

const UserLiveIcon = ({ size = 13, color = 'currentColor', style }: { size?: number; color?: string; style?: React.CSSProperties }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={style}>
        <circle cx="12" cy="12" r="10" />
        <circle cx="12" cy="12" r="3" fill={color} />
    </svg>
);

const LocationPinIcon = ({ size = 14, color = 'currentColor', style }: { size?: number; color?: string; style?: React.CSSProperties }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}>
        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
        <circle cx="12" cy="10" r="3" />
    </svg>
);

const OrganizationIcon = ({ size = 14, color = 'currentColor', style }: { size?: number; color?: string; style?: React.CSSProperties }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}>
        <rect x="4" y="2" width="16" height="20" rx="2" ry="2" />
        <path d="M9 22v-4h6v4" />
        <path d="M8 6h.01M16 6h.01M8 10h.01M16 10h.01M8 14h.01M16 14h.01" />
    </svg>
);

const ChevronIcon = ({ isExpanded, size = 14, color = 'currentColor' }: { isExpanded: boolean; size?: number; color?: string }) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke={color}
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{
            transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s ease-in-out',
            flexShrink: 0
        }}
    >
        <polyline points="6 9 12 15 18 9" />
    </svg>
);

const BoltIcon = ({ size = 16, color = '#0284c7' }: { size?: number; color?: string }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" fill={color} />
    </svg>
);

const OnboardingActionRequiredSection: React.FC<OnboardingActionRequiredSectionProps> = ({
    activeActions,
    isLoading = false,
    onGoToLocation,
    onGoToOrganization
}) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCategory, setSelectedCategory] = useState<ActionCategoryKey>('all');
    const [viewMode, setViewMode] = useState<ActionViewMode>('location');
    const [locationSort, setLocationSort] = useState<LocationSortOption>('alpha');
    const [itemsPerPage, setItemsPerPage] = useState<number>(10);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [collapsedLocationIds, setCollapsedLocationIds] = useState<Record<string, boolean>>({});
    const [isSectionCollapsed, setIsSectionCollapsed] = useState<boolean>(false);

    // 1. Enrich all actions with priority & category
    const enrichedActions = useMemo<EnrichedActionItem[]>(() => {
        return activeActions.map(item => {
            const enrichment = getActionEnrichment(item);
            return {
                ...item,
                ...enrichment
            };
        });
    }, [activeActions]);

    // 2. Count totals per category
    const categoryCounts = useMemo(() => {
        const counts: Record<ActionCategoryKey, number> = {
            all: enrichedActions.length,
            critical: 0,
            launch: 0,
            outbound: 0,
            qa: 0,
            invites: 0
        };

        enrichedActions.forEach(item => {
            if (counts[item.categoryKey] !== undefined) {
                counts[item.categoryKey]++;
            }
        });

        return counts;
    }, [enrichedActions]);

    // 3. Filter actions by search query and category
    const filteredActions = useMemo(() => {
        const query = searchQuery.trim().toLowerCase();

        return enrichedActions.filter(item => {
            // Category filter
            if (selectedCategory !== 'all' && item.categoryKey !== selectedCategory) {
                return false;
            }

            // Search filter
            if (query) {
                const appName = (item.app?.name || '').toLowerCase();
                const prefix = (item.action?.prefix || '').toLowerCase();
                const suffix = (item.action?.suffix || '').toLowerCase();
                const btnText = (item.action?.buttonText || '').toLowerCase();
                const catLabel = item.categoryLabel.toLowerCase();

                const matches = appName.includes(query)
                    || prefix.includes(query)
                    || suffix.includes(query)
                    || btnText.includes(query)
                    || catLabel.includes(query);

                if (!matches) return false;
            }

            return true;
        });
    }, [enrichedActions, selectedCategory, searchQuery]);

    // 4. Group actions by Location
    const groupedByLocation = useMemo(() => {
        const groups: Array<{
            appId: string;
            appName: string;
            isOrg: boolean;
            app: ApplicationModel;
            actions: EnrichedActionItem[];
            maxPriorityScore: number;
            highestPriorityTier: ActionPriorityTier;
            highestPriorityLabel: string;
            highestPriorityBadge: { bg: string; text: string; border: string };
        }> = [];

        const map = new Map<string, EnrichedActionItem[]>();

        filteredActions.forEach(item => {
            const key = item.isOrg ? 'org' : (item.app?.id || 'unknown');
            if (!map.has(key)) {
                map.set(key, []);
            }
            map.get(key)!.push(item);
        });

        map.forEach((actions, appId) => {
            const first = actions[0];
            const isOrg = !!first.isOrg;
            const app = first.app;
            const appName = isOrg ? 'Organization Onboarding' : (app?.name || 'Unnamed Location');

            // Sort actions inside each location by priority descending
            const sortedActions = [...actions].sort((a, b) => b.priorityScore - a.priorityScore);

            const topAction = sortedActions[0];

            groups.push({
                appId,
                appName,
                isOrg,
                app,
                actions: sortedActions,
                maxPriorityScore: topAction.priorityScore,
                highestPriorityTier: topAction.priorityTier,
                highestPriorityLabel: topAction.categoryLabel,
                highestPriorityBadge: topAction.categoryBadgeColor
            });
        });

        // Sort locations based on selected location sort
        groups.sort((a, b) => {
            // Org is always on top
            if (a.isOrg && !b.isOrg) return -1;
            if (!a.isOrg && b.isOrg) return 1;

            if (locationSort === 'priority') {
                if (b.maxPriorityScore !== a.maxPriorityScore) {
                    return b.maxPriorityScore - a.maxPriorityScore;
                }
                return a.appName.localeCompare(b.appName);
            }

            if (locationSort === 'count') {
                if (b.actions.length !== a.actions.length) {
                    return b.actions.length - a.actions.length;
                }
                return a.appName.localeCompare(b.appName);
            }

            // Default: alphabetical A-Z
            return a.appName.localeCompare(b.appName);
        });

        return groups;
    }, [filteredActions, locationSort]);

    // 5. Group actions by Priority Tier
    const groupedByPriority = useMemo(() => {
        const tiers: Array<{
            tier: ActionPriorityTier;
            title: string;
            iconColor: string;
            badgeBg: string;
            badgeText: string;
            badgeBorder: string;
            actions: EnrichedActionItem[];
        }> = [
            {
                tier: 'critical',
                title: 'Critical & Urgent Actions',
                iconColor: '#ef4444',
                badgeBg: '#fee2e2',
                badgeText: '#b91c1c',
                badgeBorder: '#fca5a5',
                actions: []
            },
            {
                tier: 'high',
                title: 'Launch Readiness & Outbound Access',
                iconColor: '#10b981',
                badgeBg: '#dcfce7',
                badgeText: '#15803d',
                badgeBorder: '#86efac',
                actions: []
            },
            {
                tier: 'medium',
                title: 'QA, Automated Testing & Org Setup',
                iconColor: '#0284c7',
                badgeBg: '#e0f2fe',
                badgeText: '#0369a1',
                badgeBorder: '#7dd3fc',
                actions: []
            },
            {
                tier: 'standard',
                title: 'Invitations & Email Reminders',
                iconColor: '#64748b',
                badgeBg: '#f1f5f9',
                badgeText: '#334155',
                badgeBorder: '#cbd5e1',
                actions: []
            }
        ];

        filteredActions.forEach(action => {
            const targetTier = tiers.find(t => t.tier === action.priorityTier);
            if (targetTier) {
                targetTier.actions.push(action);
            }
        });

        // Inside each tier, sort by location name A-Z
        tiers.forEach(t => {
            t.actions.sort((a, b) => {
                if (a.isOrg && !b.isOrg) return -1;
                if (!a.isOrg && b.isOrg) return 1;
                return (a.app?.name || '').localeCompare(b.app?.name || '');
            });
        });

        return tiers.filter(t => t.actions.length > 0);
    }, [filteredActions]);

    // 6. Pagination calculations
    const totalLocationPages = Math.ceil(groupedByLocation.length / itemsPerPage) || 1;
    const paginatedLocationGroups = useMemo(() => {
        if (itemsPerPage >= 9999) return groupedByLocation;
        const start = (currentPage - 1) * itemsPerPage;
        return groupedByLocation.slice(start, start + itemsPerPage);
    }, [groupedByLocation, currentPage, itemsPerPage]);

    // Flat list pagination
    const totalFlatPages = Math.ceil(filteredActions.length / itemsPerPage) || 1;
    const paginatedFlatActions = useMemo(() => {
        if (itemsPerPage >= 9999) return filteredActions;
        const start = (currentPage - 1) * itemsPerPage;
        return filteredActions.slice(start, start + itemsPerPage);
    }, [filteredActions, currentPage, itemsPerPage]);

    // Toggle expand/collapse for a location
    const toggleLocationCollapse = (appId: string) => {
        setCollapsedLocationIds(prev => ({
            ...prev,
            [appId]: !prev[appId]
        }));
    };

    // Expand all / Collapse all locations
    const handleExpandAll = () => {
        setCollapsedLocationIds({});
    };

    const handleCollapseAll = () => {
        const next: Record<string, boolean> = {};
        groupedByLocation.forEach(g => {
            next[g.appId] = true;
        });
        setCollapsedLocationIds(next);
    };

    // Total unique locations with actions
    const totalUniqueLocations = useMemo(() => {
        const set = new Set<string>();
        activeActions.forEach(a => {
            if (!a.isOrg && a.app?.id) {
                set.add(a.app.id);
            }
        });
        return set.size;
    }, [activeActions]);

    if (isLoading) {
        return (
            <ActionContainer>
                <HeaderRow>
                    <HeaderTitle>
                        <BoltIcon />
                        <span>Action Required</span>
                    </HeaderTitle>
                </HeaderRow>
                <div style={{ position: 'relative', height: '120px', width: '100%' }}>
                    <Loader />
                </div>
            </ActionContainer>
        );
    }

    if (activeActions.length === 0) {
        return (
            <SuccessActionContainer>
                <EmptySuccessBlock role="status" aria-live="polite">
                    <SuccessIconWrapper>
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                            <polyline points="22 4 12 14.01 9 11.01" />
                        </svg>
                    </SuccessIconWrapper>
                    <SuccessTitle>All Caught Up!</SuccessTitle>
                </EmptySuccessBlock>
            </SuccessActionContainer>
        );
    }

    const categoryPills: Array<{ key: ActionCategoryKey; label: string }> = [
        { key: 'all', label: 'All' },
        { key: 'critical', label: 'Critical / Feedback' },
        { key: 'launch', label: 'Launch & Access' },
        { key: 'outbound', label: 'Outbound Review' },
        { key: 'qa', label: 'QA & Testing' },
        { key: 'invites', label: 'Invites & Reminders' }
    ];

    return (
        <ActionContainer>
            {/* Top Header */}
            <HeaderRow>
                <HeaderLeft>
                    <HeaderTitle onClick={() => setIsSectionCollapsed(prev => !prev)} style={{ cursor: 'pointer', userSelect: 'none' }}>
                        <BoltIcon />
                        <span>Action Required</span>
                        <TotalCountBadge>
                            {`${activeActions.length} Action${activeActions.length !== 1 ? 's' : ''}${totalUniqueLocations > 0 ? ` · ${totalUniqueLocations} Location${totalUniqueLocations !== 1 ? 's' : ''}` : ''}`}
                        </TotalCountBadge>
                    </HeaderTitle>
                </HeaderLeft>

                <HeaderRight>
                    <CollapseSectionButton
                        type="button"
                        onClick={() => setIsSectionCollapsed(prev => !prev)}
                        aria-label={isSectionCollapsed ? 'Expand Action Required' : 'Collapse Action Required'}
                    >
                        <span>{isSectionCollapsed ? 'Expand' : 'Collapse'}</span>
                        <ChevronIcon isExpanded={!isSectionCollapsed} size={13} color="#64748b" />
                    </CollapseSectionButton>
                </HeaderRight>
            </HeaderRow>

            {!isSectionCollapsed && (
                <>
                    {/* Category Filter Pills Bar */}
                    <CategoryFilterBar>
                        {categoryPills.map(pill => {
                            const count = categoryCounts[pill.key];
                            if (pill.key !== 'all' && count === 0) return null;
                            const isSelected = selectedCategory === pill.key;

                            return (
                                <CategoryPillButton
                                    key={pill.key}
                                    type="button"
                                    isSelected={isSelected}
                                    onClick={() => {
                                        setSelectedCategory(pill.key);
                                        setCurrentPage(1);
                                    }}
                                >
                                    <span>{pill.label}</span>
                                    <PillBadge isSelected={isSelected}>{count}</PillBadge>
                                </CategoryPillButton>
                            );
                        })}
                    </CategoryFilterBar>

                    {/* Filter & Controls Toolbar */}
                    <ToolbarContainer>
                        <ToolbarSearchWrapper>
                            <SearchInputContainer>
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
                                    <circle cx="11" cy="11" r="8" />
                                    <line x1="21" y1="21" x2="16.65" y2="16.65" />
                                </svg>
                                <ToolbarSearchInput
                                    type="search"
                                    value={searchQuery}
                                    onChange={(e) => {
                                        setSearchQuery(e.target.value);
                                        setCurrentPage(1);
                                    }}
                                    placeholder="Search by location name or action..."
                                    aria-label="Search actions"
                                />
                                {searchQuery && (
                                    <ClearSearchBtn
                                        type="button"
                                        onClick={() => {
                                            setSearchQuery('');
                                            setCurrentPage(1);
                                        }}
                                        title="Clear search"
                                    >
                                        &times;
                                    </ClearSearchBtn>
                                )}
                            </SearchInputContainer>
                        </ToolbarSearchWrapper>

                        <ToolbarControlsRight>
                            {/* View Mode Toggle */}
                            <ControlSelectGroup>
                                <ControlLabel>View:</ControlLabel>
                                <ControlSelect
                                    value={viewMode}
                                    onChange={(e) => {
                                        setViewMode(e.target.value as ActionViewMode);
                                        setCurrentPage(1);
                                    }}
                                    aria-label="Action View Mode"
                                >
                                    <option value="location">Group by Location</option>
                                    <option value="priority">Group by Priority</option>
                                    <option value="flat">Compact List</option>
                                </ControlSelect>
                            </ControlSelectGroup>

                            {/* Location Sort Selector (shown when in location group mode) */}
                            {viewMode === 'location' && (
                                <ControlSelectGroup>
                                    <ControlLabel>Sort:</ControlLabel>
                                    <ControlSelect
                                        value={locationSort}
                                        onChange={(e) => {
                                            setLocationSort(e.target.value as LocationSortOption);
                                            setCurrentPage(1);
                                        }}
                                        aria-label="Sort Locations"
                                    >
                                        <option value="alpha">Location (A–Z)</option>
                                        <option value="priority">Highest Urgency</option>
                                        <option value="count">Most Actions</option>
                                    </ControlSelect>
                                </ControlSelectGroup>
                            )}

                            {/* Expand / Collapse All */}
                            {viewMode === 'location' && groupedByLocation.length > 1 && (
                                <ExpandCollapseBtnRow>
                                    <MiniTextButton type="button" onClick={handleExpandAll}>
                                        Expand All
                                    </MiniTextButton>
                                    <span style={{ color: '#cbd5e1' }}>|</span>
                                    <MiniTextButton type="button" onClick={handleCollapseAll}>
                                        Collapse All
                                    </MiniTextButton>
                                </ExpandCollapseBtnRow>
                            )}
                        </ToolbarControlsRight>
                    </ToolbarContainer>

                    {/* Filtered Empty State */}
                    {filteredActions.length === 0 && (
                        <FilteredEmptyState>
                            <p style={{ margin: 0, fontWeight: 600, color: '#475569' }}>No actions match your search or filter.</p>
                            <p style={{ margin: '6px 0 0', fontSize: '13px', color: '#94a3b8' }}>
                                Try clearing your search query or selecting a different category filter.
                            </p>
                            {(searchQuery || selectedCategory !== 'all') && (
                                <div style={{ marginTop: '14px' }}>
                                    <Button
                                        themes={['secondary-small']}
                                        text="Clear Filters"
                                        type="button"
                                        onClick={() => {
                                            setSearchQuery('');
                                            setSelectedCategory('all');
                                            setCurrentPage(1);
                                        }}
                                    />
                                </div>
                            )}
                        </FilteredEmptyState>
                    )}

                    {/* VIEW MODE 1: GROUPED BY LOCATION (Default) */}
                    {viewMode === 'location' && filteredActions.length > 0 && (
                        <LocationListContainer>
                            {paginatedLocationGroups.map((group) => {
                                const isCollapsed = !!collapsedLocationIds[group.appId];
                                const isOrg = group.isOrg;
                                const app = group.app;

                                return (
                                    <LocationCard key={group.appId} isOrg={isOrg}>
                                        {/* Location Card Header */}
                                        <LocationCardHeader
                                            isCollapsed={isCollapsed}
                                            onClick={() => toggleLocationCollapse(group.appId)}
                                        >
                                            <LocationHeaderLeft>
                                                <LocationIconWrapper isOrg={isOrg}>
                                                    {isOrg ? <OrganizationIcon color="#0284c7" /> : <LocationPinIcon color="#0284c7" />}
                                                </LocationIconWrapper>

                                                <LocationTitleBlock>
                                                    <LocationNameLink
                                                        href="#"
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            if (isOrg) {
                                                                onGoToOrganization?.();
                                                            } else if (app?.id) {
                                                                onGoToLocation?.(app.id);
                                                            }
                                                        }}
                                                        title={`Go to ${group.appName}`}
                                                    >
                                                        {group.appName}
                                                    </LocationNameLink>

                                                    {/* Live / Target Launch Date Badge */}
                                                    {!isOrg && (
                                                        <LaunchDateChip isLive={!!app?.isLive}>
                                                            <UserLiveIcon
                                                                size={11}
                                                                color={app?.isLive ? color_colors_growth : color_colors_gold}
                                                                style={{ marginRight: '4px' }}
                                                            />
                                                            <span>
                                                                {app?.isLive
                                                                    ? (app.launchDate ? `Live ${moment.utc(app.launchDate).format('MM/DD/YYYY')}` : 'Live')
                                                                    : (app?.targetLaunchDate ? `Target: ${moment.utc(app.targetLaunchDate).format('MM/DD/YYYY')}` : 'Target: TBD')
                                                                }
                                                            </span>
                                                        </LaunchDateChip>
                                                    )}
                                                </LocationTitleBlock>
                                            </LocationHeaderLeft>

                                            <LocationHeaderRight>
                                                {/* Highest Urgency Tag */}
                                                <UrgencyBadge
                                                    bg={group.highestPriorityBadge.bg}
                                                    textColor={group.highestPriorityBadge.text}
                                                    borderColor={group.highestPriorityBadge.border}
                                                >
                                                    {group.highestPriorityLabel}
                                                </UrgencyBadge>

                                                {/* Action Count Pill */}
                                                <ActionCountPill>
                                                    {`${group.actions.length} Action${group.actions.length !== 1 ? 's' : ''}`}
                                                </ActionCountPill>

                                                <ChevronIcon isExpanded={!isCollapsed} size={15} color="#64748b" />
                                            </LocationHeaderRight>
                                        </LocationCardHeader>

                                        {/* Location Action Items Body */}
                                        {!isCollapsed && (
                                            <LocationActionItemsList>
                                                {group.actions.map((item, idx) => (
                                                    <ActionItemRow
                                                        key={item.id}
                                                        isLast={idx === group.actions.length - 1}
                                                        priorityTier={item.priorityTier}
                                                    >
                                                        <ActionItemLeft>
                                                            <ActionCategoryTag
                                                                bg={item.categoryBadgeColor.bg}
                                                                textColor={item.categoryBadgeColor.text}
                                                                borderColor={item.categoryBadgeColor.border}
                                                            >
                                                                {item.categoryLabel}
                                                            </ActionCategoryTag>

                                                            <ActionDescription>
                                                                <span>{item.action.prefix}</span>
                                                                {!item.isOrg && (
                                                                    <LocationLinkInline
                                                                        href="#"
                                                                        onClick={(e) => {
                                                                            e.preventDefault();
                                                                            if (item.app?.id) {
                                                                                onGoToLocation?.(item.app.id);
                                                                            }
                                                                        }}
                                                                    >
                                                                        {item.app?.name}
                                                                    </LocationLinkInline>
                                                                )}
                                                                {item.isOrg && (
                                                                    <LocationLinkInline
                                                                        href="#"
                                                                        onClick={(e) => {
                                                                            e.preventDefault();
                                                                            onGoToOrganization?.();
                                                                        }}
                                                                    >
                                                                        {item.app?.name}
                                                                    </LocationLinkInline>
                                                                )}
                                                                {item.action.suffix && <span>{item.action.suffix}</span>}
                                                            </ActionDescription>
                                                        </ActionItemLeft>

                                                        <ActionItemRight>
                                                            {item.action.buttonText && (
                                                                <Button
                                                                    themes={(item.action.themes as any) || ['primary-small']}
                                                                    text={item.action.buttonText}
                                                                    type="button"
                                                                    disabled={item.action.disabled}
                                                                    onClick={item.action.onClick}
                                                                />
                                                            )}
                                                        </ActionItemRight>
                                                    </ActionItemRow>
                                                ))}
                                            </LocationActionItemsList>
                                        )}
                                    </LocationCard>
                                );
                            })}

                            {/* Pagination for Location Groups */}
                            {totalLocationPages > 1 && (
                                <PaginationFooter>
                                    <PaginationInfo>
                                        {`Showing ${(currentPage - 1) * itemsPerPage + 1}–${Math.min(currentPage * itemsPerPage, groupedByLocation.length)} of ${groupedByLocation.length} locations with actions`}
                                    </PaginationInfo>

                                    <PaginationControls>
                                        <PageSizeSelectorWrapper>
                                            <span style={{ fontSize: '12px', color: '#64748b' }}>Per page:</span>
                                            <PageSizeSelect
                                                value={itemsPerPage}
                                                onChange={(e) => {
                                                    setItemsPerPage(Number(e.target.value));
                                                    setCurrentPage(1);
                                                }}
                                                aria-label="Locations per page"
                                            >
                                                <option value={10}>10</option>
                                                <option value={25}>25</option>
                                                <option value={50}>50</option>
                                                <option value={9999}>All</option>
                                            </PageSizeSelect>
                                        </PageSizeSelectorWrapper>

                                        <PaginationButtons>
                                            <PageNavButton
                                                type="button"
                                                disabled={currentPage === 1}
                                                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                                aria-label="Previous page"
                                            >
                                                &lsaquo; Prev
                                            </PageNavButton>

                                            {Array.from({ length: totalLocationPages }, (_, i) => i + 1)
                                                .filter(p => p === 1 || p === totalLocationPages || Math.abs(p - currentPage) <= 1)
                                                .map((pageNum, idx, arr) => {
                                                    const prev = arr[idx - 1];
                                                    const showEllipsis = prev && pageNum - prev > 1;

                                                    return (
                                                        <React.Fragment key={pageNum}>
                                                            {showEllipsis && <span style={{ padding: '0 4px', color: '#94a3b8' }}>...</span>}
                                                            <PageNumberButton
                                                                type="button"
                                                                isActive={currentPage === pageNum}
                                                                onClick={() => setCurrentPage(pageNum)}
                                                            >
                                                                {pageNum}
                                                            </PageNumberButton>
                                                        </React.Fragment>
                                                    );
                                                })}

                                            <PageNavButton
                                                type="button"
                                                disabled={currentPage === totalLocationPages}
                                                onClick={() => setCurrentPage(prev => Math.min(totalLocationPages, prev + 1))}
                                                aria-label="Next page"
                                            >
                                                Next &rsaquo;
                                            </PageNavButton>
                                        </PaginationButtons>
                                    </PaginationControls>
                                </PaginationFooter>
                            )}
                        </LocationListContainer>
                    )}

                    {/* VIEW MODE 2: GROUPED BY PRIORITY */}
                    {viewMode === 'priority' && filteredActions.length > 0 && (
                        <PriorityGroupsContainer>
                            {groupedByPriority.map(group => (
                                <PriorityLaneCard key={group.tier} tier={group.tier}>
                                    <PriorityLaneHeader
                                        badgeBg={group.badgeBg}
                                        badgeText={group.badgeText}
                                        badgeBorder={group.badgeBorder}
                                    >
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <span style={{ fontSize: '15px', fontWeight: 700, color: '#1e293b' }}>
                                                {group.title}
                                            </span>
                                            <UrgencyBadge
                                                bg={group.badgeBg}
                                                textColor={group.badgeText}
                                                borderColor={group.badgeBorder}
                                            >
                                                {`${group.actions.length} Action${group.actions.length !== 1 ? 's' : ''}`}
                                            </UrgencyBadge>
                                        </div>
                                    </PriorityLaneHeader>

                                    <LocationActionItemsList>
                                        {group.actions.map((item, idx) => (
                                            <ActionItemRow
                                                key={item.id}
                                                isLast={idx === group.actions.length - 1}
                                                priorityTier={item.priorityTier}
                                            >
                                                <ActionItemLeft>
                                                    <ActionCategoryTag
                                                        bg={item.categoryBadgeColor.bg}
                                                        textColor={item.categoryBadgeColor.text}
                                                        borderColor={item.categoryBadgeColor.border}
                                                    >
                                                        {item.categoryLabel}
                                                    </ActionCategoryTag>

                                                    <ActionDescription>
                                                        <span>{item.action.prefix}</span>
                                                        {!item.isOrg && (
                                                            <LocationLinkInline
                                                                href="#"
                                                                onClick={(e) => {
                                                                    e.preventDefault();
                                                                    if (item.app?.id) {
                                                                        onGoToLocation?.(item.app.id);
                                                                    }
                                                                }}
                                                            >
                                                                {item.app?.name}
                                                            </LocationLinkInline>
                                                        )}
                                                        {item.isOrg && (
                                                            <LocationLinkInline
                                                                href="#"
                                                                onClick={(e) => {
                                                                    e.preventDefault();
                                                                    onGoToOrganization?.();
                                                                }}
                                                            >
                                                                {item.app?.name}
                                                            </LocationLinkInline>
                                                        )}
                                                        {item.action.suffix && <span>{item.action.suffix}</span>}
                                                    </ActionDescription>
                                                </ActionItemLeft>

                                                <ActionItemRight>
                                                    {item.action.buttonText && (
                                                        <Button
                                                            themes={(item.action.themes as any) || ['primary-small']}
                                                            text={item.action.buttonText}
                                                            type="button"
                                                            disabled={item.action.disabled}
                                                            onClick={item.action.onClick}
                                                        />
                                                    )}
                                                </ActionItemRight>
                                            </ActionItemRow>
                                        ))}
                                    </LocationActionItemsList>
                                </PriorityLaneCard>
                            ))}
                        </PriorityGroupsContainer>
                    )}

                    {/* VIEW MODE 3: FLAT COMPACT LIST */}
                    {viewMode === 'flat' && filteredActions.length > 0 && (
                        <LocationListContainer>
                            <FlatListCard>
                                <LocationActionItemsList>
                                    {paginatedFlatActions.map((item, idx) => (
                                        <ActionItemRow
                                            key={item.id}
                                            isLast={idx === paginatedFlatActions.length - 1}
                                            priorityTier={item.priorityTier}
                                        >
                                            <ActionItemLeft>
                                                <FlatItemIndexBadge>{(currentPage - 1) * itemsPerPage + idx + 1}</FlatItemIndexBadge>
                                                <ActionCategoryTag
                                                    bg={item.categoryBadgeColor.bg}
                                                    textColor={item.categoryBadgeColor.text}
                                                    borderColor={item.categoryBadgeColor.border}
                                                >
                                                    {item.categoryLabel}
                                                </ActionCategoryTag>

                                                <ActionDescription>
                                                    <span>{item.action.prefix}</span>
                                                    {!item.isOrg && (
                                                        <LocationLinkInline
                                                            href="#"
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                if (item.app?.id) {
                                                                    onGoToLocation?.(item.app.id);
                                                                }
                                                            }}
                                                        >
                                                            {item.app?.name}
                                                        </LocationLinkInline>
                                                    )}
                                                    {item.isOrg && (
                                                        <LocationLinkInline
                                                            href="#"
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                onGoToOrganization?.();
                                                            }}
                                                        >
                                                            {item.app?.name}
                                                        </LocationLinkInline>
                                                    )}
                                                    {item.action.suffix && <span>{item.action.suffix}</span>}
                                                </ActionDescription>
                                            </ActionItemLeft>

                                            <ActionItemRight>
                                                {item.action.buttonText && (
                                                    <Button
                                                        themes={(item.action.themes as any) || ['primary-small']}
                                                        text={item.action.buttonText}
                                                        type="button"
                                                        disabled={item.action.disabled}
                                                        onClick={item.action.onClick}
                                                    />
                                                )}
                                            </ActionItemRight>
                                        </ActionItemRow>
                                    ))}
                                </LocationActionItemsList>
                            </FlatListCard>

                            {/* Pagination for Flat List */}
                            {totalFlatPages > 1 && (
                                <PaginationFooter>
                                    <PaginationInfo>
                                        {`Showing ${(currentPage - 1) * itemsPerPage + 1}–${Math.min(currentPage * itemsPerPage, filteredActions.length)} of ${filteredActions.length} actions`}
                                    </PaginationInfo>

                                    <PaginationControls>
                                        <PageSizeSelectorWrapper>
                                            <span style={{ fontSize: '12px', color: '#64748b' }}>Per page:</span>
                                            <PageSizeSelect
                                                value={itemsPerPage}
                                                onChange={(e) => {
                                                    setItemsPerPage(Number(e.target.value));
                                                    setCurrentPage(1);
                                                }}
                                                aria-label="Actions per page"
                                            >
                                                <option value={10}>10</option>
                                                <option value={25}>25</option>
                                                <option value={50}>50</option>
                                                <option value={9999}>All</option>
                                            </PageSizeSelect>
                                        </PageSizeSelectorWrapper>

                                        <PaginationButtons>
                                            <PageNavButton
                                                type="button"
                                                disabled={currentPage === 1}
                                                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                                aria-label="Previous page"
                                            >
                                                &lsaquo; Prev
                                            </PageNavButton>

                                            {Array.from({ length: totalFlatPages }, (_, i) => i + 1)
                                                .filter(p => p === 1 || p === totalFlatPages || Math.abs(p - currentPage) <= 1)
                                                .map((pageNum, idx, arr) => {
                                                    const prev = arr[idx - 1];
                                                    const showEllipsis = prev && pageNum - prev > 1;

                                                    return (
                                                        <React.Fragment key={pageNum}>
                                                            {showEllipsis && <span style={{ padding: '0 4px', color: '#94a3b8' }}>...</span>}
                                                            <PageNumberButton
                                                                type="button"
                                                                isActive={currentPage === pageNum}
                                                                onClick={() => setCurrentPage(pageNum)}
                                                            >
                                                                {pageNum}
                                                            </PageNumberButton>
                                                        </React.Fragment>
                                                    );
                                                })}

                                            <PageNavButton
                                                type="button"
                                                disabled={currentPage === totalFlatPages}
                                                onClick={() => setCurrentPage(prev => Math.min(totalFlatPages, prev + 1))}
                                                aria-label="Next page"
                                            >
                                                Next &rsaquo;
                                            </PageNavButton>
                                        </PaginationButtons>
                                    </PaginationControls>
                                </PaginationFooter>
                            )}
                        </LocationListContainer>
                    )}
                </>
            )}
        </ActionContainer>
    );
};

export default OnboardingActionRequiredSection;

/* =========================================================================
   EMOTION STYLES (Clean, Modern, Space-Efficient & Mobile Responsive)
   ========================================================================= */

const fontFamilyStack = `'Muli', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif`;

const ActionContainer = styled.div`
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-left: 5px solid #0284c7;
    border-radius: 8px;
    padding: 20px;
    margin-bottom: 24px;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.04), 0 1px 2px 0 rgba(0, 0, 0, 0.02);
    transition: all 0.2s ease-in-out;
    font-family: ${fontFamilyStack};

    @media (max-width: 768px) {
        padding: 14px;
        border-radius: 6px;
    }
`;

const SuccessActionContainer = styled(ActionContainer)`
    padding: 18px 24px;
    border: 1px solid #d7e8df;
    border-left: 5px solid #10b981;
    background: #ffffff;

    @media (max-width: 768px) {
        padding: 14px;
    }
`;

const HeaderRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-bottom: 1px solid #e2e8f0;
    padding-bottom: 14px;
    flex-wrap: wrap;
    gap: 10px;
    font-family: ${fontFamilyStack};
`;

const HeaderLeft = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
`;

const HeaderRight = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const HeaderTitle = styled.h3`
    font-family: ${fontFamilyStack};
    font-size: 16px;
    font-weight: 700;
    color: #1e293b;
    margin: 0;
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
`;

const TotalCountBadge = styled.span`
    background: #e0f2fe;
    color: #0369a1;
    border: 1px solid #bae6fd;
    font-family: ${fontFamilyStack};
    font-size: 11px;
    font-weight: 700;
    padding: 3px 10px;
    border-radius: 12px;
    letter-spacing: 0.2px;
`;

const CollapseSectionButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: transparent;
    border: 1px solid #cbd5e1;
    border-radius: 6px;
    padding: 5px 10px;
    font-family: ${fontFamilyStack};
    font-size: 12px;
    font-weight: 600;
    color: #475569;
    cursor: pointer;
    transition: all 0.15s ease;

    &:hover {
        background: #f8fafc;
        border-color: #94a3b8;
        color: #1e293b;
    }

    &:focus {
        outline: none;
        border-color: #0284c7;
        box-shadow: 0 0 0 2px rgba(2, 132, 199, 0.15);
    }
`;

/* Category Filter Bar */
const CategoryFilterBar = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 14px;
    padding-bottom: 4px;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    font-family: ${fontFamilyStack};

    &::-webkit-scrollbar {
        height: 4px;
    }
    &::-webkit-scrollbar-thumb {
        background: #cbd5e1;
        border-radius: 2px;
    }
`;

const CategoryPillButton = styled.button<{ isSelected: boolean }>`
    display: inline-flex;
    align-items: center;
    padding: 6px 12px;
    border-radius: 20px;
    font-family: ${fontFamilyStack};
    font-size: 12px;
    font-weight: 600;
    white-space: nowrap;
    cursor: pointer;
    border: 1px solid ${props => props.isSelected ? '#0284c7' : '#e2e8f0'};
    background: ${props => props.isSelected ? '#0284c7' : '#ffffff'};
    color: ${props => props.isSelected ? '#ffffff' : '#475569'};
    transition: all 0.15s ease-in-out;
    box-shadow: ${props => props.isSelected ? '0 1px 3px rgba(2, 132, 199, 0.25)' : 'none'};

    &:hover {
        background: ${props => props.isSelected ? '#0369a1' : '#f8fafc'};
        border-color: ${props => props.isSelected ? '#0369a1' : '#cbd5e1'};
        color: ${props => props.isSelected ? '#ffffff' : '#1e293b'};
    }

    &:focus {
        outline: none;
        box-shadow: 0 0 0 2px rgba(2, 132, 199, 0.2);
    }
`;

const PillBadge = styled.span<{ isSelected: boolean }>`
    margin-left: 6px;
    background: ${props => props.isSelected ? 'rgba(255, 255, 255, 0.25)' : '#e2e8f0'};
    color: ${props => props.isSelected ? '#ffffff' : '#334155'};
    font-family: ${fontFamilyStack};
    font-size: 11px;
    font-weight: 700;
    padding: 1px 6px;
    border-radius: 10px;
`;

/* Toolbar */
const ToolbarContainer = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 14px;
    margin-bottom: 16px;
    gap: 12px;
    flex-wrap: wrap;
    font-family: ${fontFamilyStack};

    @media (max-width: 768px) {
        flex-direction: column;
        align-items: stretch;
    }
`;

const ToolbarSearchWrapper = styled.div`
    flex: 1;
    min-width: 220px;

    @media (max-width: 768px) {
        min-width: 100%;
    }
`;

const SearchInputContainer = styled.div`
    position: relative;
    width: 100%;
`;

const ToolbarSearchInput = styled.input`
    width: 100%;
    padding: 8px 32px 8px 34px;
    border: 1px solid #cbd5e1;
    border-radius: 6px;
    font-family: ${fontFamilyStack};
    font-size: 13px;
    color: #1e293b;
    background: #ffffff;
    box-sizing: border-box;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;

    &:focus {
        outline: none;
        border-color: #0284c7;
        box-shadow: 0 0 0 2px rgba(2, 132, 199, 0.15);
    }

    &::placeholder {
        color: #94a3b8;
    }
`;

const ClearSearchBtn = styled.button`
    position: absolute;
    right: 8px;
    top: 50%;
    transform: translateY(-50%);
    background: transparent;
    border: none;
    font-family: ${fontFamilyStack};
    font-size: 16px;
    color: #94a3b8;
    cursor: pointer;
    line-height: 1;
    padding: 4px;

    &:hover {
        color: #475569;
    }
`;

const ToolbarControlsRight = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
    font-family: ${fontFamilyStack};

    @media (max-width: 768px) {
        justify-content: space-between;
    }
`;

const ControlSelectGroup = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-family: ${fontFamilyStack};
`;

const ControlLabel = styled.span`
    font-family: ${fontFamilyStack};
    font-size: 12px;
    font-weight: 600;
    color: #64748b;
`;

const ControlSelect = styled.select`
    background: #ffffff;
    border: 1px solid #cbd5e1;
    border-radius: 6px;
    padding: 6px 10px;
    font-family: ${fontFamilyStack};
    font-size: 12px;
    font-weight: 600;
    color: #334155;
    cursor: pointer;
    outline: none;
    transition: border-color 0.15s ease;

    &:focus {
        border-color: #0284c7;
        box-shadow: 0 0 0 2px rgba(2, 132, 199, 0.15);
    }
`;

const ExpandCollapseBtnRow = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-family: ${fontFamilyStack};
    font-size: 12px;
`;

const MiniTextButton = styled.button`
    background: transparent;
    border: none;
    color: #0284c7;
    font-family: ${fontFamilyStack};
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    padding: 0;

    &:hover {
        text-decoration: underline;
        color: #0369a1;
    }
`;

/* Empty & Filtered States */
const EmptySuccessBlock = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 16px;
    padding: 0;
    box-sizing: border-box;
    font-family: ${fontFamilyStack};

    h4 {
        margin: 0;
    }

    @media (max-width: 768px) {
        gap: 12px;
    }
`;

const SuccessIconWrapper = styled.div`
    width: 38px;
    height: 38px;
    border-radius: 50%;
    background: #dcfce7;
    border: 1px solid #a7f3d0;
    color: #059669;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
`;

const SuccessTitle = styled.h4`
    font-family: ${fontFamilyStack};
    font-size: 15px;
    font-weight: 700;
    color: #1e293b;
    margin: 0;

    && {
        margin: 0;
        font-size: 15px;
        font-weight: 700;
        color: #1e293b;
    }
`;

const FilteredEmptyState = styled.div`
    padding: 32px 20px;
    text-align: center;
    background: #f8fafc;
    border: 1px dashed #cbd5e1;
    border-radius: 8px;
    margin-top: 8px;
    font-family: ${fontFamilyStack};
`;

/* Location Cards List */
const LocationListContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    font-family: ${fontFamilyStack};
`;

const LocationCard = styled.div<{ isOrg?: boolean }>`
    background: ${props => props.isOrg ? '#f8fafd' : '#ffffff'};
    border: 1px solid ${props => props.isOrg ? '#bfdbfe' : '#e2e8f0'};
    border-left: ${props => props.isOrg ? '4px solid #0284c7' : '1px solid #e2e8f0'};
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.02);
    transition: border-color 0.15s ease, box-shadow 0.15s ease;
    font-family: ${fontFamilyStack};

    &:hover {
        border-color: #cbd5e1;
        box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.04);
    }
`;

const LocationCardHeader = styled.div<{ isCollapsed: boolean }>`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 16px;
    background: ${props => props.isCollapsed ? 'transparent' : '#f8fafc'};
    border-bottom: ${props => props.isCollapsed ? 'none' : '1px solid #e2e8f0'};
    cursor: pointer;
    user-select: none;
    flex-wrap: wrap;
    gap: 10px;
    transition: background 0.15s ease;
    font-family: ${fontFamilyStack};

    &:hover {
        background: #f1f5f9;
    }
`;

const LocationHeaderLeft = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    flex: 1;
    min-width: 200px;
`;

const LocationIconWrapper = styled.div<{ isOrg?: boolean }>`
    width: 28px;
    height: 28px;
    border-radius: 6px;
    background: ${props => props.isOrg ? '#eff6ff' : '#f0f9ff'};
    border: 1px solid ${props => props.isOrg ? '#bfdbfe' : '#bae6fd'};
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
`;

const LocationTitleBlock = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
`;

const LocationNameLink = styled.a`
    font-family: ${fontFamilyStack};
    font-size: 14px;
    font-weight: 700;
    color: #0284c7;
    text-decoration: none;

    &:hover {
        text-decoration: underline;
        color: #0369a1;
    }
`;

const LocationHeaderRight = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
`;

const LaunchDateChip = styled.span<{ isLive?: boolean }>`
    display: inline-flex;
    align-items: center;
    font-family: ${fontFamilyStack};
    font-size: 11px;
    font-weight: 600;
    padding: 2px 8px;
    border-radius: 10px;
    background: ${props => props.isLive ? '#f0fdf4' : '#fffbeb'};
    color: ${props => props.isLive ? '#15803d' : '#b45309'};
    border: 1px solid ${props => props.isLive ? '#bbf7d0' : '#fde68a'};
`;

const UrgencyBadge = styled.span<{ bg: string; textColor: string; borderColor: string }>`
    font-family: ${fontFamilyStack};
    font-size: 11px;
    font-weight: 700;
    padding: 2px 8px;
    border-radius: 12px;
    background: ${props => props.bg};
    color: ${props => props.textColor};
    border: 1px solid ${props => props.borderColor};
    letter-spacing: 0.1px;
`;

const ActionCountPill = styled.span`
    font-family: ${fontFamilyStack};
    font-size: 11px;
    font-weight: 700;
    padding: 2px 8px;
    border-radius: 12px;
    background: #f1f5f9;
    color: #475569;
    border: 1px solid #cbd5e1;
`;

/* Action Item Rows */
const LocationActionItemsList = styled.div`
    display: flex;
    flex-direction: column;
    font-family: ${fontFamilyStack};
`;

const ActionItemRow = styled.div<{ isLast: boolean; priorityTier?: ActionPriorityTier }>`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px 16px;
    background: #ffffff;
    border-bottom: ${props => props.isLast ? 'none' : '1px solid #f1f5f9'};
    gap: 12px;
    flex-wrap: wrap;
    transition: background 0.15s ease;
    font-family: ${fontFamilyStack};

    &:hover {
        background: #f8fafc;
    }

    @media (max-width: 768px) {
        flex-direction: column;
        align-items: stretch;
        gap: 8px;
    }
`;

const ActionItemLeft = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    flex: 1;
    min-width: 220px;
    flex-wrap: wrap;
`;

const ActionCategoryTag = styled.span<{ bg: string; textColor: string; borderColor: string }>`
    font-family: ${fontFamilyStack};
    font-size: 11px;
    font-weight: 700;
    padding: 2px 8px;
    border-radius: 4px;
    background: ${props => props.bg};
    color: ${props => props.textColor};
    border: 1px solid ${props => props.borderColor};
    flex-shrink: 0;
`;

const ActionDescription = styled.div`
    font-family: ${fontFamilyStack};
    font-size: 13px;
    font-weight: 600;
    color: #334155;
    line-height: 1.4;
`;

const LocationLinkInline = styled.a`
    font-family: ${fontFamilyStack};
    color: #0284c7;
    font-weight: 700;
    text-decoration: none;
    margin: 0 4px;

    &:hover {
        text-decoration: underline;
        color: #0369a1;
    }
`;

const ActionItemRight = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    flex-shrink: 0;

    @media (max-width: 768px) {
        justify-content: stretch;
        width: 100%;

        & > button {
            width: 100%;
        }
    }
`;

/* Priority Grouping Mode */
const PriorityGroupsContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    font-family: ${fontFamilyStack};
`;

const PriorityLaneCard = styled.div<{ tier: ActionPriorityTier }>`
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.02);
    font-family: ${fontFamilyStack};
`;

const PriorityLaneHeader = styled.div<{ badgeBg: string; badgeText: string; badgeBorder: string }>`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px 16px;
    background: #f8fafc;
    border-bottom: 1px solid #e2e8f0;
    font-family: ${fontFamilyStack};
`;

/* Flat List Card */
const FlatListCard = styled.div`
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    overflow: hidden;
    font-family: ${fontFamilyStack};
`;

const FlatItemIndexBadge = styled.span`
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: #0284c7;
    color: #ffffff;
    font-family: ${fontFamilyStack};
    font-size: 11px;
    font-weight: 700;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
`;

/* Pagination */
const PaginationFooter = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 6px 4px;
    flex-wrap: wrap;
    gap: 10px;
    font-family: ${fontFamilyStack};

    @media (max-width: 768px) {
        flex-direction: column;
        align-items: stretch;
        gap: 8px;
    }
`;

const PaginationInfo = styled.span`
    font-family: ${fontFamilyStack};
    font-size: 12px;
    color: #64748b;
    font-weight: 500;
`;

const PaginationControls = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
    font-family: ${fontFamilyStack};

    @media (max-width: 768px) {
        justify-content: space-between;
    }
`;

const PageSizeSelectorWrapper = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-family: ${fontFamilyStack};
`;

const PageSizeSelect = styled.select`
    background: #ffffff;
    border: 1px solid #cbd5e1;
    border-radius: 6px;
    padding: 4px 6px;
    font-family: ${fontFamilyStack};
    font-size: 12px;
    color: #334155;
    cursor: pointer;
    outline: none;

    &:focus {
        border-color: #0284c7;
        box-shadow: 0 0 0 2px rgba(2, 132, 199, 0.15);
    }
`;

const PaginationButtons = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-family: ${fontFamilyStack};
`;

const PageNavButton = styled.button`
    background: #ffffff;
    border: 1px solid #cbd5e1;
    border-radius: 6px;
    padding: 4px 10px;
    font-family: ${fontFamilyStack};
    font-size: 12px;
    font-weight: 600;
    color: #334155;
    cursor: pointer;
    transition: all 0.15s ease;

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }

    &:not(:disabled):hover {
        background: #f8fafc;
        border-color: #94a3b8;
    }

    &:focus {
        outline: none;
        border-color: #0284c7;
        box-shadow: 0 0 0 2px rgba(2, 132, 199, 0.15);
    }
`;

const PageNumberButton = styled.button<{ isActive: boolean }>`
    background: ${props => props.isActive ? '#0284c7' : '#ffffff'};
    color: ${props => props.isActive ? '#ffffff' : '#334155'};
    border: 1px solid ${props => props.isActive ? '#0284c7' : '#cbd5e1'};
    border-radius: 6px;
    min-width: 28px;
    height: 28px;
    font-family: ${fontFamilyStack};
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition: all 0.15s ease;

    &:hover {
        background: ${props => props.isActive ? '#0369a1' : '#f8fafc'};
        border-color: ${props => props.isActive ? '#0369a1' : '#94a3b8'};
    }

    &:focus {
        outline: none;
        box-shadow: 0 0 0 2px rgba(2, 132, 199, 0.2);
    }
`;
